import { createSlice } from "@reduxjs/toolkit";
import { CreateAppointmentResponse } from "../../models/Appointment.model";
import { RootState } from "../store";
import {
  createAppointmentType,
  createOrUpdateAppointment,
  deleteAppointmentType,
  updateAppointmentStatus,
  getAppointmentTypes,
} from "../thunks/appointmentThunks";
import { getAppointment } from "../thunks/getAppointmentThunks";
import { AppointmentProject } from "../../models/GetAppointmentsByProject.model";
import { getAppointmentsByProject } from "../thunks/getAppointmentsByProjectThunks";
import { AppointmentTypeDTO } from "../../models/AppointmentType.model";
import { NormalizedAppointmentDTO } from "../../models/GetAppointment.model";

export interface AppointmentState {
  loading: boolean;
  error: string | null;
  response: CreateAppointmentResponse | null;
  selectedAppointment: NormalizedAppointmentDTO | null;
  projectAppointments: AppointmentProject[];
  loadingProjectAppointments: boolean;
  projectAppointmentsError: string | null;
  appointmentTypes: AppointmentTypeDTO[];
}

const initialState: AppointmentState = {
  loading: false,
  error: null,
  response: null,
  selectedAppointment: null,
  projectAppointments: [],
  loadingProjectAppointments: false,
  projectAppointmentsError: null,
  appointmentTypes: [],
};

const appointmentSlice = createSlice({
  name: "appointment",
  initialState,
  reducers: {
    clearSelectedAppointment(state) {
      state.selectedAppointment = null;
    },
    clearProjectAppointments(state) {
      state.projectAppointments = [];
      state.projectAppointmentsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOrUpdateAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.response = null;
      })
      .addCase(createOrUpdateAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.response = action.payload;
      })
      .addCase(createOrUpdateAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה ביצירת פגישה";
      })
      // getAppointment thunk
      .addCase(getAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedAppointment = null;
      })
      .addCase(getAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAppointment = action.payload.appointment;
      })
      .addCase(getAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה בקבלת פרטי פגישה";
        state.selectedAppointment = null;
      })
      .addCase(updateAppointmentStatus.pending, (state) => {
        // state.loading = true;
        state.error = null;
      })
      .addCase(updateAppointmentStatus.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.error = null;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(updateAppointmentStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה בעדכון סטטוס פגישה";
      })
      .addCase(getAppointmentsByProject.pending, (state) => {
        state.loadingProjectAppointments = true;
        state.projectAppointmentsError = null;
      })
      .addCase(getAppointmentsByProject.fulfilled, (state, action) => {
        state.loadingProjectAppointments = false;
        state.projectAppointments = action.payload.appointments || [];
      })
      .addCase(getAppointmentsByProject.rejected, (state, action) => {
        state.loadingProjectAppointments = false;
        state.projectAppointmentsError =
          action.payload || "שגיאה בקבלת פגישות לפי פרויקט";
        state.projectAppointments = [];
      })
      .addCase(getAppointmentTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAppointmentTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.appointmentTypes = action.payload || [];
      })
      .addCase(getAppointmentTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה בקבלת סוגי פגישות";
      })
      .addCase(createAppointmentType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAppointmentType.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createAppointmentType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה ביצירת סוג פגישה";
      })
      .addCase(deleteAppointmentType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAppointmentType.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteAppointmentType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה במחיקת סוג פגישה";
      });
  },
});

export const { clearSelectedAppointment, clearProjectAppointments } =
  appointmentSlice.actions;
export default appointmentSlice.reducer;
export const selectAppointment = (state: RootState) => state.appointment;
